import { datadogRum } from "@datadog/browser-rum";
import { getProfileName } from "PFCore/helpers/profile";
import { sanitizeSensitiveParams } from "PFCore/helpers/url_sanitizer";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { CurrentAccount, CurrentProfile } from "PFTypes";
import { useEffect } from "react";
import { useMount } from "react-use";

const TRACED_DOMAIN_SUFFIXES = [
  "profinda.com",
  "profinda-staging.com",
  "profinda-integration.com",
  "profinda-uat.com",
  "profinda-uat.us",
  "profinda.us"
];

const isTracedUrl = (url: string) => {
  let parsed: URL;
  try {
    parsed = new URL(url, window.location.href);
  } catch {
    return false;
  }

  return (
    parsed.protocol === "https:" &&
    TRACED_DOMAIN_SUFFIXES.some((suffix) => parsed.hostname.endsWith(`.${suffix}`))
  );
};

export const useDataDogRum = () => {
  useEffect(() => {
    if (
      !window.PF.config.datadog_client_token ||
      !window.PF.config.datadog_application_id ||
      !window.PF.config.datadog_site
    ) {
      return;
    }

    datadogRum.init({
      applicationId: window.PF.config.datadog_application_id,
      clientToken: window.PF.config.datadog_client_token,
      site: window.PF.config.datadog_site,
      service: "ui",
      env: window.PF.config.datadog_environment,
      version: window.PF.config.ui_version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      defaultPrivacyLevel: "mask-user-input",
      allowedTracingUrls: [{ match: isTracedUrl, propagatorTypes: ["datadog" as const] }],
      beforeSend: (event) => {
        if (event.view && event.view.url) {
          event.view.url = sanitizeSensitiveParams(event.view.url);
        }
        if (event.view && event.view.referrer) {
          event.view.referrer = sanitizeSensitiveParams(event.view.referrer);
        }

        if (event.type === "resource" && event.resource && event.resource.url) {
          event.resource.url = sanitizeSensitiveParams(event.resource.url);
        }

        if (event.type === "action" && event.action && event.action.target?.name) {
          event.action.target.name = sanitizeSensitiveParams(event.action.target.name);
        }

        if (event.type === "error" && event.error && event.error.resource?.url) {
          event.error.resource.url = sanitizeSensitiveParams(event.error.resource.url);
        }

        return true;
      }
    });
  }, [
    window.PF.config.environment,
    window.PF.config.datadog_environment,
    window.PF.config.datadog_client_token,
    window.PF.config.datadog_application_id,
    window.PF.config.datadog_site
  ]);
};

const dataDogSetUser = (profile: CurrentProfile, account: CurrentAccount) => {
  const { permissionsGroup } = profile;
  datadogRum.setUser({
    id: String(profile.id),
    name: getProfileName(profile),
    email: profile.email,
    domain: account.full_domain,
    permissions_group_id: permissionsGroup.id,
    permissions_group_name: permissionsGroup.name
  });
};

export const useDataDogRumForCurrentProfile = () => {
  const { data: currentProfile } = useCurrentProfile();
  const { data: currentAccount } = useCurrentAccount();

  useMount(() => {
    dataDogSetUser(currentProfile, currentAccount);
  });
};

export const dataDogRemoveUser = () => datadogRum.clearUser();
